
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import BarsAndClubs from "./pages/BarsAndClubs";
import Promoters from "./pages/Promoters";
import { DashboardLayout } from "./pages/dashboard/DashboardLayout";
import { DashboardHome } from "./pages/dashboard/DashboardHome";
import { SettingsPage } from "./pages/dashboard/SettingsPage";
import { TalentPage } from "./pages/dashboard/TalentPage";
import { AnalyticsPage } from "./pages/dashboard/AnalyticsPage";
import { ProfilePage } from "./pages/dashboard/ProfilePage";
import { PromotionsPage } from "./pages/dashboard/PromotionsPage";
import { SupportPage } from "./pages/dashboard/SupportPage";
import { ClubProfilePage } from "./pages/dashboard/ClubProfilePage";
import { EventsPage } from "./pages/dashboard/events/EventsPage";
import { BookingsPage } from "./pages/dashboard/bookings/BookingsPage";
import { GigsPage } from "./pages/dashboard/gigs/GigsPage";
import { SavedPage } from "./pages/dashboard/saved/SavedPage";
import { TicketsPage } from "./pages/dashboard/tickets/TicketsPage";
import { SetsPage } from "./pages/dashboard/sets/SetsPage";
import { StaffPage } from "./pages/dashboard/staff/StaffPage";
import FeatureShowcase from "./pages/FeatureShowcase";
import BarStaff from "./pages/BarStaff";
import Security from "./pages/Security";
import SecurityCheckIn from "./pages/SecurityCheckIn";
import ClubEntry from "./pages/ClubEntry";
import { VenueAnalyticsPage } from "./pages/dashboard/venue/VenueAnalyticsPage";
import { VenueProfilePage } from "./pages/dashboard/venue/VenueProfilePage";
import { VenueCalendarPage } from "./pages/dashboard/venue/VenueCalendarPage";
import { VenueStaffPage } from "./pages/dashboard/venue/VenueStaffPage";
import { BartenderSchedulePage } from "./pages/dashboard/bartender/BartenderSchedulePage";
import { BartenderCertificationsPage } from "./pages/dashboard/bartender/BartenderCertificationsPage";
import { BartenderReportsPage } from "./pages/dashboard/bartender/BartenderReportsPage";
import SecurityAccessPage from "./pages/dashboard/security/SecurityAccessPage";
import { SecuritySchedulePage } from "./pages/dashboard/security/SecuritySchedulePage";
import { SecurityReportsPage } from "./pages/dashboard/security/SecurityReportsPage";
import NotFound from "./pages/NotFound";
import AuthPage from "./pages/Auth/AuthPage";
import AccountSetupWizard from "./pages/Auth/AccountSetupWizard";
import { AuthProvider } from "./context/AuthContext";
import "./App.css";
import { useEffect } from "react";
import { supabase } from "./integrations/supabase/client";
import { useToast } from "./hooks/use-toast";
import VoiceInterface from "@/components/ai/VoiceInterface";
import { ProtectedRoute } from "./components/routing/ProtectedRoute";
import { ClubModeProvider } from "./context/ClubModeContext";
const PageLoader = () => (
  <div className="flex min-h-screen w-full bg-black text-white items-center justify-center">
    <div className="animate-pulse">Loading...</div>
  </div>
);

export default function App() {
  const { toast } = useToast();

  useEffect(() => {
    const cleanupSpecificEmail = async () => {
      try {
        console.log("Running one-time cleanup for specific email");
        const emailToClean = "brettfulmer+dj@gmail.com";
        
        // Check if this email exists in Auth first
        try {
          const { data, error: listUsersError } = await supabase.auth.admin.listUsers();
          
          if (listUsersError) {
            console.error("Error listing users:", listUsersError);
            // Even if we can't list users, try the cleanup function
          } else if (data && data.users) {
            console.log("Checking if email exists in Auth system...");
            // Define a safer type approach for the user object
            interface UserWithEmail {
              email?: string;
              [key: string]: any;
            }
            
            // Use type assertion with safety checks
            const userWithEmail = data.users.find((u: any) => {
              if (u && typeof u === 'object') {
                const userObj = u as UserWithEmail;
                return userObj.email === emailToClean;
              }
              return false;
            });
            
            if (userWithEmail) {
              console.log("Found user with email in Auth system, will clean up");
            }
          }
        } catch (authError) {
          console.error("Error accessing auth admin API:", authError);
          // Continue with cleanup even if we can't check auth users
        }
        
        // Call our cleanup function
        const { error } = await supabase.functions.invoke("delete-user", {
          body: { 
            email_to_clean: emailToClean
          },
        });
        
        if (error) {
          console.error("Error cleaning up specific email:", error);
          return;
        }
        
        console.log("Successfully cleaned up email:", emailToClean);
        toast({
          title: "System Maintenance",
          description: "Email records cleanup completed successfully",
        });
      } catch (error) {
        console.error("Error in cleanup process:", error);
      }
    };
    
    cleanupSpecificEmail();
  }, [toast]);

  return (
    <Router>
      <AuthProvider>
        <ClubModeProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/account-setup" element={<AccountSetupWizard />} />
              <Route path="/bars-and-clubs" element={<BarsAndClubs />} />
              <Route path="/promoters" element={<Promoters />} />
              <Route path="/features" element={<FeatureShowcase />} />
              <Route path="/barstaff" element={<BarStaff />} />
              <Route path="/security" element={<Security />} />
              <Route path="/security-check-in" element={<SecurityCheckIn />} />
              <Route path="/club-entry" element={<ClubEntry />} />
              <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<DashboardHome />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="talent" element={<TalentPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="promotions" element={<PromotionsPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="club-profile" element={<ClubProfilePage />} />
                <Route path="events" element={<EventsPage />} />
                <Route path="bookings" element={<BookingsPage />} />
                <Route path="gigs" element={<GigsPage />} />
                <Route path="saved" element={<SavedPage />} />
                <Route path="tickets" element={<TicketsPage />} />
                <Route path="sets" element={<SetsPage />} />
                <Route path="staff" element={<StaffPage />} />
                <Route path="venue/analytics" element={<VenueAnalyticsPage />} />
                <Route path="venue/profile" element={<VenueProfilePage />} />
                <Route path="venue/calendar" element={<VenueCalendarPage />} />
                <Route path="venue/staff" element={<VenueStaffPage />} />
                <Route path="bartender/schedule" element={<BartenderSchedulePage />} />
                <Route path="bartender/certifications" element={<BartenderCertificationsPage />} />
                <Route path="bartender/reports" element={<BartenderReportsPage />} />
                <Route path="security/access" element={<SecurityAccessPage />} />
                <Route path="security/schedule" element={<SecuritySchedulePage />} />
                <Route path="security/reports" element={<SecurityReportsPage />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <VoiceInterface />
        </ClubModeProvider>
      </AuthProvider>
    </Router>
  );
}
